import * as React from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { Box, TextInput, Group, Button, NumberInput, Select, PasswordInput, Switch } from '@mantine/core'
import { useForm, isNotEmpty, isEmail } from "@mantine/form";
import axios from 'axios';
import type { IResponse } from "../../types/api";
import { showMessage } from "../../lib/notification";
import { CLIENT_ROUTE, API_ROUTE, CLIENT_TITLE, roleCodes, UserRoleCode } from "./constants";
import { IconChevronDown } from '@tabler/icons-react';
import produce from "immer";
import { FileWithPath, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import FileUpload from "../../components/file-upload";
import Page from "../../components/layout/Page";
import { useFormStyles } from "../../styles/form";
import { assignFiles } from "../../utils/form";

interface IUser {
    createdAt: string;
    email: string;
    isActive: boolean;
    isDeleted: boolean;
    loginCount: number;
    name: string;
    phoneNumber: string | number;
    roleCode: UserRoleCode;
    roleName: string;
    roleValue: number;
    updatedAt: string;
    _id: string;
    profilePicture: string;
}

interface FormValues extends Pick<IUser, 'email' | 'name' | 'phoneNumber' | 'roleCode' > {
    profilePicture: string | FileWithPath | null;
    password: string;
}

interface IParams extends Omit<FormValues, 'profilePicture' | 'coverPicture' | 'password'> {
    profilePicture?: string;
    password?: string;
}

const EditUser = () => {

    const { state: id = null } = useLocation();

    const { classes } = useFormStyles();
    const navigate = useNavigate();

    const form = useForm<FormValues>({
        initialValues: {
            email: '',
            name: '',
            phoneNumber: '',
            roleCode: 'admin',
            profilePicture: null,
            password: '',
        },

        validate: {
            email: isEmail('Имэйл буруу байна'),
            name: isNotEmpty('Нэр оруулна уу'),
        },
    });

    React.useEffect(() => {
        const fetchData = async () => {
            const { data } = await axios.get<IResponse<IUser>>(`${API_ROUTE}/${id}`);
            const values = produce(data.result, draft => {
                if (draft.phoneNumber) {
                    draft.phoneNumber = Number(draft.phoneNumber);
                }
            })
            form.setValues(values);
            form.resetDirty();
        }
        void fetchData();
    }, []);

    const handleSubmit = async (values: typeof form.values) => {
        const files = await assignFiles({
            profilePicture: values.profilePicture
        }, 'user')

        const params: IParams = {
            ...values,
            ...files,
            password: form.isDirty('password') && values.password ? values.password : undefined
        }

        const { data } = await axios.post<IResponse<any>>(`${API_ROUTE}/${id}`, params);
        if (data.statusCode === 200) {
            navigate(CLIENT_ROUTE);
            showMessage('Амжилттай', 'green')
        } else {
            showMessage('Алдаа гарлаа')
        }
    }

    return (
        <Page>
            <Page.Header title={`${CLIENT_TITLE} засах`} backRoute={CLIENT_ROUTE}/>
            <Page.Body>
                <Box sx={{ maxWidth: 500 }} mx="auto">

                    <form onSubmit={form.onSubmit(handleSubmit)} className={classes.form}>
                        <TextInput
                            withAsterisk
                            autoComplete='off'
                            label="Имэйл"
                            placeholder="Имэйл оруулна уу"
                            {...form.getInputProps('email')}
                        />

                        <TextInput
                            withAsterisk
                            autoComplete='off'
                            label="Нэр"
                            placeholder="Нэр оруулна уу"
                            {...form.getInputProps('name')}
                        />

                        <NumberInput
                            hideControls
                            label="Утасны дугаар"
                            placeholder="Утасны дугаар оруулна уу"
                            {...form.getInputProps('phoneNumber')}
                        />

                        <Select
                            label="Роль код"
                            rightSection={<IconChevronDown size={14} />}
                            rightSectionWidth={30}
                            styles={{ rightSection: { pointerEvents: 'none' } }}
                            data={roleCodes}
                            {...form.getInputProps('roleCode')}
                        />

                        <FileUpload
                            value={form.values.profilePicture}
                            onChange={(value) => {
                                form.setFieldValue('profilePicture', value);
                            }}
                            label='Зураг'
                            accept={IMAGE_MIME_TYPE}
                        />

                        <PasswordInput
                            withAsterisk
                            label="Нууц үг"
                            placeholder="******"
                            {...form.getInputProps('password')}
                        />

                        <Switch
                            label="Идэвхтэй эсэх"
                            {...form.getInputProps('isActive', { type: 'checkbox' })}
                        />
                    </form>
                </Box>
            </Page.Body>
            <Page.Footer>
                <Group position="right" mt="md">
                    <Button color='color-secondary' onClick={() => form.onSubmit(handleSubmit)()}>Хадгалах</Button>
                </Group>
            </Page.Footer>
        </Page>
    );
};

export default EditUser;
